'use client';

import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import Link from 'next/link';
import {
  Card,
  Button,
  Space,
  Typography,
  Alert,
  Input,
  InputNumber,
  Tag,
  Modal,
  Tabs,
  Row,
  Col,
  Table,
  Tooltip,
} from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import { toast } from 'react-toastify';
import {
  CheckOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  RollbackOutlined,
  FilterOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/components/AuthContext';
import { PRStatus, PRItemStatus } from '@prisma/client';
import type { PRRecord, PRItemRecord } from '@/lib/mockPrStore';
import { PastelStatusBadge } from '@/components/PastelBadge';
import { calculateAggregatedPRStatus } from '@/services/prService';
import PRCreateEditModal from '@/components/PRCreateEditModal';
import { useDebounce } from '@/hooks/useDebounce';

const { Title, Text, Paragraph } = Typography;

interface WorkingItemDecision {
  itemId: string;
  itemCode: string;
  itemName: string;
  category: string;
  uomCode: string;
  requestedQty: number;
  approvedQty: number;
  verdict: 'PENDING' | 'APPROVED' | 'REJECTED';
  reason?: string;
}

interface PRSummaryStats {
  totalPRs: number;
  pendingApproval: number;
  approved: number;
  drafts: number;
}

function PurchaseRequestsContent() {
  const router = useRouter();
  const { hasRole, hasPermission, user } = useAuth();
  const isApprover = hasRole(['OWNER', 'ADMIN']);
  const canCreate = hasPermission('pr:create');

  // Server-side pagination & filter states
  const [prs, setPrs] = useState<PRRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 400);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const [summary, setSummary] = useState<PRSummaryStats>({
    totalPRs: 0,
    pendingApproval: 0,
    approved: 0,
    drafts: 0,
  });

  // Create / Edit PR Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingPR, setEditingPR] = useState<PRRecord | null>(null);

  // Fetch paginated PRs from API
  const fetchServerData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('pageSize', String(pageSize));

      if (activeTab !== 'ALL') {
        params.append('status', activeTab);
      }
      if (debouncedSearch.trim()) {
        params.append('search', debouncedSearch.trim());
      }

      const res = await fetch(`/api/purchase-requests?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setPrs(data.data || []);
        if (data.pagination) {
          setTotalCount(data.pagination.total || 0);
        }
        if (data.summary) {
          setSummary(data.summary);
        }
      } else {
        toast.error(data.error || 'Gagal memuat data Purchase Request');
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Koneksi bermasalah';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, activeTab, debouncedSearch]);

  // Reset to page 1 when search or tab filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeTab]);

  // Fetch when page, pageSize, filter or debounced query updates
  useEffect(() => {
    fetchServerData();
  }, [fetchServerData]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
    if (pagination.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setPage(1);
    }
  };

  // Table Columns Definition
  const columns = [
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>No. PR</span>,
      dataIndex: 'prNumber',
      key: 'prNumber',
      width: 190,
      render: (text: string, r: PRRecord) => {
        const isDraftWithoutNumber = r.status === PRStatus.DRAFT && (!r.prNumber || r.prNumber === '[Draf Belum Terbit]');
        return (
          <Link
            href={`/purchase-requests/${r.id}`}
            style={{ fontWeight: 700, color: '#4F46E5', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
          >
            <FileTextOutlined style={{ color: '#818CF8' }} />
            {isDraftWithoutNumber ? (
              <span style={{ color: '#D97706', fontWeight: 600 }}>[Draf Belum Terbit]</span>
            ) : (
              <span>{text}</span>
            )}
            {r.isRevision && (
              <Tag color="warning" style={{ fontSize: 10, padding: '0 4px', margin: 0, borderRadius: 4, fontWeight: 700 }}>
                Revisi
              </Tag>
            )}
          </Link>
        );
      },
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Judul / Keperluan Pengadaan</span>,
      key: 'title',
      render: (_: unknown, r: PRRecord) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0F172A', fontSize: 13 }}>
            {r.title || '-'}
          </div>
          {r.notes && (
            <div
              style={{
                fontSize: 11,
                color: '#64748B',
                marginTop: 2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 340,
              }}
            >
              {r.notes}
            </div>
          )}
        </div>
      ),
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Tanggal</span>,
      dataIndex: 'date',
      key: 'date',
      width: 130,
      render: (val: string) => (
        <span style={{ fontSize: 12, color: '#475569' }}>
          {new Date(val).toLocaleDateString('id-ID', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Pemohon & Departemen</span>,
      key: 'requester',
      width: 220,
      render: (_: unknown, r: PRRecord) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1E293B', fontSize: 12 }}>
            {r.requesterName}
          </div>
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
            {r.department}
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Item Material</span>,
      key: 'items',
      width: 170,
      render: (_: unknown, r: PRRecord) => (
        <div>
          <span style={{ fontWeight: 700, color: '#334155', fontSize: 12 }}>
            {r.items.length} Item SKU
          </span>
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
            {Array.from(new Set(r.items.map((it) => it.category))).slice(0, 2).join(', ')}
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Status PR</span>,
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: (_: unknown, r: PRRecord) => {
        const totalItems = r.items?.length || 0;
        const approvedItems = r.items?.filter((it) => it.status === PRItemStatus.APPROVED).length || 0;
        const rejectedItems = r.items?.filter((it) => it.status === PRItemStatus.REJECTED).length || 0;
        const isApproved = r.status === PRStatus.APPROVED || (r.status as string) === 'PARTIALLY_APPROVED';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <PastelStatusBadge status={isApproved ? PRStatus.APPROVED : r.status} size="medium" />
            {isApproved && (
              <Tooltip title={`Total Item: ${totalItems} • Disetujui: ${approvedItems} • Ditolak: ${rejectedItems}`}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    padding: '2px 8px',
                    borderRadius: 4,
                    background: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                  }}
                >
                  <span style={{ color: '#4F46E5' }}>{totalItems}</span>
                  <span style={{ color: '#94A3B8' }}>/</span>
                  <span style={{ color: '#16A34A' }}>{approvedItems}</span>
                  <span style={{ color: '#94A3B8' }}>/</span>
                  <span style={{ color: '#DC2626' }}>{rejectedItems}</span>
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      {/* Header Banner */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 4 }}>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: '#4F46E5',
              background: '#EEF2FF',
              border: '1px solid #C7D2FE',
              padding: '2px 8px',
              borderRadius: 6,
              display: 'inline-block',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            Pengelolaan & Evaluasi Pengadaan
          </span>
        </div>

        <Title
          level={2}
          style={{
            margin: 0,
            fontWeight: 800,
            color: '#0F172A',
            letterSpacing: '-0.02em',
            fontSize: 'clamp(20px, 4.5vw, 26px)',
            lineHeight: 1.25,
          }}
        >
          Purchase Requests (PR)
        </Title>

        <Text style={{ color: '#64748B', fontSize: 12.5, marginTop: 4, display: 'block', lineHeight: 1.45, maxWidth: 680 }}>
          Daftar pengajuan kebutuhan barang material per departemen. Owner dapat mengevaluasi dan menyetujui kuantitas per baris item.
        </Text>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <Button
            icon={<ReloadOutlined style={{ fontSize: 13 }} />}
            onClick={fetchServerData}
            loading={loading}
            style={{
              borderRadius: 8,
              border: '1px solid #CBD5E1',
              height: 36,
              padding: '0 14px',
              fontSize: 13,
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Segarkan
          </Button>

          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined style={{ fontSize: 13 }} />}
              onClick={() => {
                setEditingPR(null);
                setCreateModalOpen(true);
              }}
              style={{
                background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                borderColor: '#4F46E5',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
                fontWeight: 600,
                borderRadius: 8,
                height: 36,
                padding: '0 16px',
                fontSize: 13,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              Buat PR Baru
            </Button>
          )}
        </div>
      </div>

      {/* KPI Stat Cards */}
      <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #F1F5F9',
              boxShadow: '0 4px 16px -2px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>
                TOTAL PR
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0F172A', marginTop: 2 }}>
                {summary.totalPRs}
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 1 }}>
                Pengajuan aktif
              </div>
            </div>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#EEF2FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#4F46E5',
                fontSize: 18,
              }}
            >
              <FileTextOutlined />
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #F1F5F9',
              boxShadow: '0 4px 16px -2px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#D97706', textTransform: 'uppercase' }}>
                MENUNGGU
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#B45309', marginTop: 2 }}>
                {summary.pendingApproval}
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 1 }}>
                Review Owner
              </div>
            </div>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#FEF3C7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#D97706',
                fontSize: 18,
              }}
            >
              <ClockCircleOutlined />
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #F1F5F9',
              boxShadow: '0 4px 16px -2px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#16A34A', textTransform: 'uppercase' }}>
                DISETUJUI
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#15803D', marginTop: 2 }}>
                {summary.approved}
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 1 }}>
                Siap PO
              </div>
            </div>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#DCFCE7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#16A34A',
                fontSize: 18,
              }}
            >
              <CheckCircleOutlined />
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #F1F5F9',
              boxShadow: '0 4px 16px -2px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#6366F1', textTransform: 'uppercase' }}>
                DRAF PR
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#4338CA', marginTop: 2 }}>
                {summary.drafts}
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 1 }}>
                Belum diajukan
              </div>
            </div>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: '#EEF2FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6366F1',
                fontSize: 18,
              }}
            >
              <PlusOutlined />
            </div>
          </div>
        </Col>
      </Row>

      {/* Main Content Card: Tabs, Search Toolbar, and Server-Side Paginated Table */}
      <Card
        variant="borderless"
        style={{
          borderRadius: 16,
          boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.04)',
          border: '1px solid #F1F5F9',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key)}
            style={{ marginBottom: 0 }}
            items={[
              { key: 'ALL', label: `Semua (${summary.totalPRs})` },
              { key: 'PENDING', label: isMobile ? `Review (${summary.pendingApproval})` : `Perlu Review (${summary.pendingApproval})` },
              { key: 'COMPLETED', label: `Disetujui (${summary.approved})` },
              { key: 'DRAFTS', label: `Draf (${summary.drafts})` },
            ]}
          />

          <div style={{ width: 340 }}>
            <Input
              placeholder="Cari no. PR, pemohon, departemen..."
              prefix={<FilterOutlined style={{ color: '#94A3B8' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 10, height: 38 }}
            />
          </div>
        </div>

        {/* Server-Side Paginated Table */}
        <Table
          rowKey="id"
          dataSource={prs}
          columns={columns}
          loading={loading}
          scroll={{ x: 780 }}
          onRow={(record) => ({
            onClick: () => router.push(`/purchase-requests/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} Pengajuan PR`,
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <InboxOutlined style={{ fontSize: 36, color: '#CBD5E1' }} />
                <div style={{ marginTop: 8, color: '#94A3B8', fontSize: 13 }}>
                  Tidak ada dokumen pengajuan PR pada kriteria filter ini.
                </div>
              </div>
            ),
          }}
        />
      </Card>

      {/* PR Create / Edit Modal */}
      <PRCreateEditModal
        open={createModalOpen}
        editRecord={editingPR}
        onClose={() => {
          setCreateModalOpen(false);
          setEditingPR(null);
        }}
        onSuccess={() => {
          fetchServerData();
        }}
      />
    </div>
  );
}

export default function PurchaseRequestsPage() {
  return <PurchaseRequestsContent />;
}
